import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { StickyNote2Outlined, EventNoteOutlined, WarningAmber } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import AddOtherEntryDialog from "../Accounts/AddOtherEntryDialog";
import { fetchOtherEntryDeletePreview } from "../Accounts/otherEntryApi";
import DeleteModal from "components/Modals/DeleteModal";
import { deleteData } from "const/axios";
import GenericPaymentsModal from "../Accounts/GenericPaymentsModal";
import LinkedModulePaymentsModal from "../Accounts/LinkedModulePaymentsModal";
import { isLinkedModuleRow, isOtherEntryRow } from "../Accounts/modulePaymentsRowUtils";
import { syncCadPayments } from "../Accounts/accountsApi";
import {
  enrichPaymentsFromNotes,
  findNoteForPayment,
  findPaymentForNote,
  isPaymentNote,
  isSameNote,
} from "../Accounts/modulePaymentNoteUtils";
import {
  hydrateModulePaymentsForRow,
  normalizeModulePayments,
  persistAccountDetailNote,
  prefetchModulePaymentsMap,
} from "../Accounts/modulePaymentsRowUtils";
import NotesModal from "../Accounts/NotesModal";
import TermNotificationsCell from "../Accounts/TermNotificationsCell";
import { fetchAccountTermNotificationCount } from "services/accountTermNotificationsApi";
import DiscussionNotesModal from "components/Modals/NotesModal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useAdmissionTerms,
  addInstallment,
  deleteInstallment,
  fetchAdmissionTerms,
} from "../Admissions/AddAdmissions/admissionApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useClientAdmissions } from "utils/studentService";
import UploadFile from "components/Input/UploadFile";
import axios from "const/axios";
import { API_URL } from "const/constants";
import DisplayDate from "components/DisplayDate";
import { formatDisplayDate, formatDisplayDateTime } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { canViewAgencyFinancials, getUserType, hasPermission } from "utils/permissionUtils";
import { accountFieldsFor } from "./accountFieldVisibility";
import { formatMoney } from "utils/utils";
import AccountVerificationChip from "../Accounts/AccountVerificationChip";
import TermScheduleTable from "../Admissions/AddAdmissions/applicationFlow/TermScheduleTable";
import { relockSavedSchedule } from "../Admissions/AddAdmissions/applicationFlow/superAdminTermEdit";
import ApplicationWithdrawPanel from "../Admissions/AddAdmissions/ApplicationWithdrawPanel";
import {
  hasPendingVerificationPayment,
  netPendingPaidFromTermPayments,
  netVerifiedPaidFromTermPayments,
  resolveTermPaidAmountFromPayments,
} from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import {
  getManageAdmissionContext,
  ManageAdmissionContextTooltip,
  manageAdmissionTabTooltipProps,
} from "../Admissions/AddAdmissions/manageAdmissionContext";
import useKothar from "context/useKothar";
import { P } from "const/permissions";
import {
  ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE,
  admissionSnapshotTotals,
  buildAdmissionInstallmentsFromTermSchedule,
  buildAdmissionValuesFromStudent,
  buildVerificationSnapshot,
  calculateRowValues,
  calculateTotals,
  createNewAccountDetail,
  getAdmissionTermSchedule,
  parseAdmissionApplicationData,
  recalculateAdmissionDetail,
  shouldShowAdmissionInAccounts,
} from "../Accounts/accountUtils";

const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Identifying detail so multiple entries of the same module can be told apart in the summary table.
const getAccountRowSubtitle = (row = {}) => {
  if (row.module === "Admission") {
    return [row.admissionValues?.course, row.admissionValues?.university]
      .filter((p) => p && String(p).trim())
      .join(" · ");
  }
  return row.linkedModuleSummary || "";
};

// Computed here too, not only in the expanded sub-table, so the Financial Snapshot is right on load.
const computeAdmissionTermTotalsFromTerms = (admissionTerms, accountDetails = []) =>
  admissionSnapshotTotals(admissionTerms, accountDetails);

const stagedAdmissionRowKey = (row, index) =>
  row?.termId || row?.uuid || row?.terms || String(index);

const ACTIVE_STAGED_VERIFICATION_STATUSES = ["PENDING", "VERIFIED", "IMPORTED"];

const isActiveStagedVerification = (status) => ACTIVE_STAGED_VERIFICATION_STATUSES.includes(status);

const stagedAdmissionVerificationKey = (studentId, row, index) =>
  studentId ? `${studentId}:${stagedAdmissionRowKey(row, index)}` : null;

const STAGED_ACCOUNT_FIELDS = [
  { key: "agreedAmount", label: "Agreed Amount" },
  { key: "cost", label: "Cost" },
  { key: "paidAmount", label: "Paid Amount" },
  { key: "referral", label: "Referral" },
  { key: "discount", label: "Discount" },
  { key: "agreed", label: "Agreed" },
];

const parseStudentAppData = (student) => {
  try {
    return JSON.parse(student?.applicationData || "{}");
  } catch {
    return {};
  }
};

const TERM_SCHEDULE_SERVER_FIELDS = [
  "term",
  "termSchedule",
  "agreedFeePerUnit",
  "agreedDiscountPerUnit",
];

const buildTermGeneralInfoFromStudent = (student, cadId = null) => {
  const fresh = parseStudentAppData(student);
  return {
    ...fresh,
    id: student.id,
    clientId: student.clientId,
    ...(cadId != null ? { clientAccountDetailId: cadId } : {}),
  };
};

const pickTermScheduleServerFields = (fresh) =>
  TERM_SCHEDULE_SERVER_FIELDS.reduce((acc, key) => {
    if (fresh[key] !== undefined) acc[key] = fresh[key];
    return acc;
  }, {});

const getApiErrorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || fallback;

const buildOfferReceivedPatchPayload = (generalInfo, student) => {
  const app = parseStudentAppData(student);
  return {
    ...app,
    ...generalInfo,
    studentId: generalInfo?.studentId || app?.studentId || student?.studentId,
    courseStartDate:
      generalInfo?.courseStartDate || app?.courseStartDate || student?.courseStartDate,
    courseEndDate: generalInfo?.courseEndDate || app?.courseEndDate || student?.courseEndDate,
    term: generalInfo?.term ?? app?.term,
    termSchedule: generalInfo?.termSchedule ?? app?.termSchedule,
  };
};

const SecondRow = ({
  accountDetails,
  parentUuid,
  admissionId,
  clientId,
  clientAccountDetailId,
  module,
  onTermTotalsChange,
  onOpenNotesModal,
}) => {
  const isAdmission = module === "Admission";
  // Account notes hold internal payment/term detail; term notes are generic verification and open.
  const canViewAccountNotes = hasPermission(P.ACCOUNTS.NOTE_READ);
  const {
    data: admissionTerms = [],
    refetch: refetchTerms,
    isLoading: termsLoading,
  } = useAdmissionTerms(isAdmission ? clientAccountDetailId : null);
  const [addInstallmentOpen, setAddInstallmentOpen] = useState(false);
  const [newInstallment, setNewInstallment] = useState({
    admissionDate: "",
    censusDate: "",
    perSemFee: "",
    materialFee: "",
  });
  const [termNotesModal, setTermNotesModal] = useState({ open: false, termIndex: -1 });
  const [accountTermNotesModal, setAccountTermNotesModal] = useState({ open: false, term: null });
  const [termNotesCounts, setTermNotesCounts] = useState({});
  const [termNotificationCounts, setTermNotificationCounts] = useState({});

  useEffect(() => {
    if (!clientId || !admissionId || !isAdmission) return;
    const termIndices =
      admissionTerms.length > 0
        ? admissionTerms.map((term) => Number(term.termNumber) - 1)
        : (accountDetails || []).map((sub, idx) => {
            const raw = sub?.terms;
            const termNum = raw
              ? Number(String(raw).trim().replace(/^T/i, "")) || idx + 1
              : idx + 1;
            return termNum - 1;
          });
    [...new Set(termIndices.filter((index) => index >= 0))].forEach((termIndex) => {
      axios
        .get(`${API_URL}/discussion/${clientId}?type=admission_term_${admissionId}_${termIndex}`)
        .then((r) => {
          const count = (r.data?.comments ?? r.data?.data ?? []).length;
          setTermNotesCounts((prev) => ({ ...prev, [termIndex]: count }));
        })
        .catch(() => {});
    });
  }, [clientId, admissionId, isAdmission, admissionTerms, accountDetails?.length]);

  useEffect(() => {
    if (!clientAccountDetailId || !clientId || !isAdmission) return;
    const termNums =
      admissionTerms.length > 0
        ? admissionTerms.map((term) => Number(term.termNumber))
        : (accountDetails || []).map((sub, idx) => {
            const raw = sub?.terms;
            return raw ? Number(String(raw).trim().replace(/^T/i, "")) || idx + 1 : idx + 1;
          });
    [...new Set(termNums.filter((n) => Number.isFinite(n) && n > 0))].forEach((termNumber) => {
      fetchAccountTermNotificationCount({
        clientAccountDetailId,
        termNumber,
        clientId,
      })
        .then((count) => setTermNotificationCounts((prev) => ({ ...prev, [termNumber]: count })))
        .catch(() => {});
    });
  }, [clientAccountDetailId, clientId, isAdmission, admissionTerms, accountDetails?.length]);

  const mergedRows = useMemo(() => {
    if (!isAdmission) {
      return accountDetails || [];
    }
    if (admissionTerms.length > 0) {
      return admissionTerms.map((term) => {
        const importedDetail = (accountDetails || []).find((d) => {
          const termNum = d.terms ? Number(String(d.terms).replace(/^T/i, "")) : null;
          return termNum === term.termNumber;
        });
        const verifiedPaid = Math.max(0, netVerifiedPaidFromTermPayments(term.payments));
        const pendingPaid = netPendingPaidFromTermPayments(term.payments);
        const resolvedPaidAmount = resolveTermPaidAmountFromPayments(
          term.payments,
          importedDetail?.paidAmount
        );
        const fee = toNumber(term.perSemFee);
        const material = toNumber(term.materialFee);
        const discount = toNumber(term.discount);
        const paid = toNumber(resolvedPaidAmount);
        const computedAgreed = fee + material - discount;
        const computedDue = computedAgreed - paid;
        const rowBase = {
          date: term.admissionDate,
          terms: `T${term.termNumber}`,
          perSemFee: term.perSemFee,
          materialFee: term.materialFee,
          discount: term.discount,
          paidAmount: resolvedPaidAmount,
          dueAmount: importedDetail?.dueAmount ?? String(computedDue),
          agreed: importedDetail?.agreed ?? String(computedAgreed),
          _imported: Boolean(term.imported),
          _termId: term.id,
          _termNumber: term.termNumber,
          _termNotes: term.notes || [],
          _paymentsCount: (term.payments || []).length,
          _verifiedPaid: verifiedPaid,
          _pendingPaid: pendingPaid,
          payments: term.payments,
        };
        if (importedDetail) {
          return {
            ...rowBase,
            ...importedDetail,
            id: importedDetail.id,
            uuid: importedDetail.uuid,
            date: importedDetail.date || importedDetail.admissionDate || term.admissionDate,
            terms: `T${term.termNumber}`,
            paidAmount: resolvedPaidAmount,
            dueAmount: importedDetail.dueAmount ?? String(computedDue),
            agreed: importedDetail.agreed ?? String(computedAgreed),
            notes: importedDetail.notes ?? [],
            notesCount: importedDetail.notesCount,
            _imported: true,
            _termId: term.id,
            _termNumber: term.termNumber,
            _paymentsCount: (term.payments || []).length,
            _verifiedPaid: verifiedPaid,
            _pendingPaid: pendingPaid,
            payments: term.payments,
          };
        }
        return {
          ...term,
          ...rowBase,
          id: undefined,
          uuid: undefined,
        };
      });
    }
    return accountDetails || [];
  }, [isAdmission, admissionTerms, accountDetails]);

  const onTermTotalsChangeRef = useRef(onTermTotalsChange);
  onTermTotalsChangeRef.current = onTermTotalsChange;

  useEffect(() => {
    if (!isAdmission || !clientAccountDetailId) return;
    if (termsLoading) return;
    const totals = computeAdmissionTermTotalsFromTerms(admissionTerms, accountDetails);
    onTermTotalsChangeRef.current?.(clientAccountDetailId, totals);
  }, [admissionTerms, accountDetails, isAdmission, clientAccountDetailId, termsLoading]);

  const subColumns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 120,
        Cell: ({ cell }) => {
          const dateValue = cell.getValue();
          return <DisplayDate value={cell.getValue()} />;
        },
      },
      {
        accessorKey: "terms",
        header: "Term",
        size: 100,
      },
      {
        accessorKey: "perSemFee",
        header: "Per Sem Fee",
        size: 120,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },

      {
        accessorKey: "materialFee",
        header: "Material Fee",
        size: 120,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },
      {
        accessorKey: "paidAmount",
        header: "Paid Amount",
        size: 120,
        Cell: ({ row, cell }) => (
          <Box>
            <span>{formatMoney(cell.getValue() || 0)}</span>
            {row.original._pendingPaid > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="warning.main"
                sx={{ display: "block", lineHeight: 1.3 }}
              >
                +{formatMoney(row.original._pendingPaid)} pending
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: "dueAmount",
        header: "Due Amount",
        size: 120,
        Cell: ({ cell }) => formatMoney(Math.max(0, toNumber(cell.getValue()))),
      },
      // The referral fee is what the agency pays a referrer, so the server sends it only to a
      // caller who may see agency money. Without the key the column would print a hollow $0.00.
      ...(canViewAgencyFinancials()
        ? [
            {
              accessorKey: "referral",
              header: "Referral",
              size: 100,
              Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
            },
          ]
        : []),

      {
        accessorKey: "discount",
        header: "Discount",
        size: 100,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },
      {
        id: "termNotifications",
        header: "Notifications",
        size: 110,
        Cell: ({ row, row: { index } }) => {
          const raw = row.original?.terms;
          const termNum = raw
            ? Number(String(raw).trim().replace(/^T/i, "")) || index + 1
            : index + 1;
          return (
            <TermNotificationsCell
              count={termNotificationCounts[termNum] || 0}
              clientAccountDetailId={clientAccountDetailId}
              termNumber={termNum}
              termLabel={raw ? `Term ${raw}` : `Term ${termNum}`}
              clientId={clientId}
              onModalClose={() =>
                fetchAccountTermNotificationCount({
                  clientAccountDetailId,
                  termNumber: termNum,
                  clientId,
                }).then((count) =>
                  setTermNotificationCounts((prev) => ({ ...prev, [termNum]: count }))
                )
              }
            />
          );
        },
      },
      {
        id: "termNotes",
        header: "Notes",
        size: 100,
        Cell: ({ row, row: { index } }) => {
          const raw = row.original?.terms;
          const termNum = raw
            ? Number(String(raw).trim().replace(/^T/i, "")) || index + 1
            : index + 1;
          const termIndex = termNum - 1;
          const termCount = termNotesCounts[termIndex] || 0;
          const subUuid = row.original.uuid || (row.original.id ? `sub-${row.original.id}` : null);
          // Admission account notes anchor to the term row (available before import); other modules
          // keep the legacy per-installment note flow.
          const termNotes = row.original._termNotes || [];
          const termNotesCount = isAdmission ? termNotes.length : row.original.notes?.length || 0;
          const rowReady = isAdmission ? Boolean(row.original._termId) : Boolean(row.original.id);
          const canUseAccountNotes = canViewAccountNotes && rowReady;
          return (
            <div className="flex items-center gap-1">
              <Tooltip
                title={
                  !canViewAccountNotes
                    ? "You need the Accounts permission to view account notes"
                    : rowReady
                    ? termNotesCount
                      ? `${termNotesCount} note(s)`
                      : "Add note"
                    : "Installment notes are available after this term is imported"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canUseAccountNotes}
                    onClick={() =>
                      isAdmission
                        ? setAccountTermNotesModal({ open: true, term: row.original })
                        : onOpenNotesModal({
                            state: true,
                            data: { ...row.original, uuid: subUuid },
                            subtable: true,
                            parentId: parentUuid,
                          })
                    }
                  >
                    <Badge
                      badgeContent={canUseAccountNotes ? termNotesCount || 0 : 0}
                      color="error"
                      max={99}
                      invisible={!canUseAccountNotes}
                      sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}
                    >
                      <EventNoteOutlined
                        fontSize="small"
                        className={canUseAccountNotes ? "text-blue-500" : "text-gray-300"}
                      />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              {admissionId && clientId && (
                <Tooltip title={termCount ? `${termCount} term note(s)` : "Add term note"}>
                  <IconButton
                    size="small"
                    onClick={() => setTermNotesModal({ open: true, termIndex })}
                  >
                    <Badge
                      badgeContent={termCount || 0}
                      color="error"
                      max={99}
                      sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}
                    >
                      <StickyNote2Outlined fontSize="small" className="text-app-text-secondary" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              {!isAdmission && (
                <Tooltip title="Remove installment">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      if (!window.confirm("Remove this installment?")) return;
                      try {
                        await deleteInstallment(clientAccountDetailId, row.original.id);
                        refetchTerms();
                      } catch (err) {
                        toast.error(
                          err?.response?.data?.message || "Failed to remove installment."
                        );
                      }
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      ...(isAdmission
        ? [
            {
              id: "termStatus",
              header: "Status",
              size: 140,
              Cell: ({ row }) =>
                row.original._imported ? (
                  <Chip label="Imported" size="small" color="success" variant="outlined" />
                ) : hasPendingVerificationPayment(row.original.payments) ? (
                  <Chip
                    label="Pending verification"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ) : (
                  <Chip label="Not imported" size="small" variant="outlined" />
                ),
            },
          ]
        : []),
    ],
    [
      parentUuid,
      admissionId,
      clientId,
      termNotesCounts,
      isAdmission,
      clientAccountDetailId,
      refetchTerms,
      onOpenNotesModal,
    ]
  );
  const subTable = useMaterialReactTable({
    columns: subColumns,
    data: mergedRows,
    enablePagination: false,
    enableRowNumbers: true,
    initialState: { density: "compact" },
    enableColumnPinning: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        minHeight: "200px",
        maxHeight: "400px",
        overflowY: "auto",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: row.original._imported === false ? { bgcolor: "rgba(255, 193, 7, 0.06)" } : {},
    }),
  });
  return (
    <>
      {!isAdmission && (
        <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setAddInstallmentOpen(true)}
          >
            Add Installment
          </Button>
        </Box>
      )}
      <MaterialReactTable table={subTable} data={mergedRows} />
      {termNotesModal.open && admissionId && clientId && (
        <DiscussionNotesModal
          open={termNotesModal.open}
          handleClose={() => {
            const closedIndex = termNotesModal.termIndex;
            setTermNotesModal({ open: false, termIndex: -1 });
            axios
              .get(
                `${API_URL}/discussion/${clientId}?type=admission_term_${admissionId}_${closedIndex}`
              )
              .then((r) => {
                const count = (r.data?.comments ?? r.data?.data ?? []).length;
                setTermNotesCounts((prev) => ({ ...prev, [closedIndex]: count }));
              })
              .catch(() => {});
          }}
          studentId={clientId}
          type={`admission_term_${admissionId}_${termNotesModal.termIndex}`}
          attachmentsEnabled={true}
        />
      )}
      {accountTermNotesModal.open && (
        <NotesModal
          open={accountTermNotesModal.open}
          handleClose={() => setAccountTermNotesModal({ open: false, term: null })}
          standalone
          standaloneParentType="ADMISSION_TERM"
          standaloneParentId={accountTermNotesModal.term?._termId}
          initialNotes={accountTermNotesModal.term?._termNotes || []}
          onChanged={refetchTerms}
        />
      )}
      {addInstallmentOpen && (
        <Dialog open maxWidth="sm" fullWidth onClose={() => setAddInstallmentOpen(false)}>
          <DialogTitle>Add Installment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newInstallment.admissionDate}
                onChange={(e) =>
                  setNewInstallment((p) => ({ ...p, admissionDate: e.target.value }))
                }
              />
              <TextField
                label="Census Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newInstallment.censusDate}
                onChange={(e) => setNewInstallment((p) => ({ ...p, censusDate: e.target.value }))}
              />
              <TextField
                label="Fee"
                type="number"
                size="small"
                value={newInstallment.perSemFee}
                onChange={(e) => setNewInstallment((p) => ({ ...p, perSemFee: e.target.value }))}
              />
              <TextField
                label="Material Fee"
                type="number"
                size="small"
                value={newInstallment.materialFee}
                onChange={(e) => setNewInstallment((p) => ({ ...p, materialFee: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddInstallmentOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await addInstallment(clientAccountDetailId, {
                    admissionDate: newInstallment.admissionDate,
                    censusDate: newInstallment.censusDate,
                    perSemFee: newInstallment.perSemFee,
                    materialFee: newInstallment.materialFee,
                  });
                  setAddInstallmentOpen(false);
                  setNewInstallment({
                    admissionDate: "",
                    censusDate: "",
                    perSemFee: "",
                    materialFee: "",
                  });
                  refetchTerms();
                } catch (err) {
                  toast.error(err?.response?.data?.message || "Failed to add installment.");
                }
              }}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

// Modules with their own dedicated payment/verification UI (e.g. Visa's Payments tab, backed by
// ModulePaymentTransaction) — the legacy generic "Payments" column/modal must stay hidden for
// these, or the two systems fight over the same ClientAccountDetail.paidAmount. "RPL Client" is the
// pre-rename legacy label a not-yet-migrated CAD row may still carry (see moduleDisplayName /
// legacyAliases on the backend); "RPL" is what synced/linked rows actually use.
const MODULES_WITH_DEDICATED_PAYMENT_FLOW = [
  "Visa",
  "Skill Assessment",
  "Insurance",
  "RPL",
  "RPL Client",
];

const ClientAccounts = ({ clientData }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [verificationEditor, setVerificationEditor] = useState({ open: false, detail: null });
  const [receiptData, setReceiptData] = useState({ receipts: [] });
  const [selectedAdmissionDetailIds, setSelectedAdmissionDetailIds] = useState([]);
  const [stagedEditor, setStagedEditor] = useState({ open: false, detail: null });
  const [stagedReceiptData, setStagedReceiptData] = useState({ receipts: [] });
  const [selectedStagedAdmissionRowKeys, setSelectedStagedAdmissionRowKeys] = useState([]);
  const [stagedAdmissionLoading, setStagedAdmissionLoading] = useState(false);
  const [admissionsForStagedEditor, setAdmissionsForStagedEditor] = useState([]);
  const [termGeneralInfoMap, setTermGeneralInfoMap] = useState({});
  const [termSavingMap, setTermSavingMap] = useState({});
  const [admissionTermTotalsMap, setAdmissionTermTotalsMap] = useState({});
  const [manageAdmissionsOpen, setManageAdmissionsOpen] = useState(false);
  const scheduleEditedSinceOpenRef = useRef(new Set());
  const [activeAdmissionTab, setActiveAdmissionTab] = useState(0);
  const [localAccountDetails, setLocalAccountDetails] = useState([]);
  const [modulePaymentsMap, setModulePaymentsMap] = useState({});
  const [paymentsModal, setPaymentsModal] = useState({ open: false, row: null });
  // Account notes hold internal payment/term detail; viewing them needs the Accounts permission.
  const canViewAccountNotes = hasPermission(P.ACCOUNTS.NOTE_READ);
  const [openNotesModal, setOpenNotesModal] = useState({
    state: false,
    data: null,
    subtable: false,
    parentId: null,
  });
  const [addOtherEntryOpen, setAddOtherEntryOpen] = useState(false);
  const [editOtherEntryRow, setEditOtherEntryRow] = useState(null);
  const [deleteOtherTarget, setDeleteOtherTarget] = useState({ state: false, id: null });
  const canEditOtherEntry = hasPermission([P.ACCOUNTS.OTHER_ENTRY_MANAGE, P.ACCOUNTS.UPDATE]);
  const canDeleteOtherEntry = hasPermission([P.ACCOUNTS.OTHER_ENTRY_MANAGE, P.ACCOUNTS.DELETE]);
  const [{ courseList, uniData }, { refetchCourseList, refetchUniData }] = useKothar();
  // Scoped to this client only — not the org-wide studentList context query, which loads
  // every admission in the org just to filter down to this one client's rows.
  const { data: clientAdmissions = [], refetch: refetchClientAdmissions } = useClientAdmissions(id);
  const userType = getUserType();
  const canSendClientAccountVerification = ["ADMIN", "ACCOUNT", "SUPER_ADMIN"].includes(userType);
  const {
    data: accountData = [],
    isFetching: isLoading,
    refetch: refetchAccountData,
  } = useQuery({
    queryKey: ["clientAccounts", id],
    queryFn: async () => {
      const res = await axios.get(`/accounts/client/${id}`);
      return res?.data?.data ?? [];
    },
    enabled: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!id) return;
    refetchAccountData().then(() => {
      queryClient.invalidateQueries({ queryKey: ["client-summary", id] });
    });
    setAdmissionTermTotalsMap({});
    setModulePaymentsMap({});
  }, [id, queryClient, refetchAccountData]);

  useEffect(() => {
    // An empty list is an answer, not a missing one: deleting the account's last entry has to clear
    // the table rather than leave the deleted row on screen until the page is reloaded.
    if (!Array.isArray(accountData?.accountDetails)) return;
    const mapped = accountData.accountDetails.map((item) => ({
      ...item,
      uuid: item.uuid || (item.id ? `row-${item.id}` : crypto.randomUUID()),
      notes: item.notes || [],
      admissionDetails: (item.admissionDetails || []).map((sub) => ({
        ...sub,
        uuid: sub.uuid || (sub.id ? `sub-${sub.id}` : crypto.randomUUID()),
        notes: sub.notes || [],
      })),
    }));
    setLocalAccountDetails(mapped);
    if (mapped.length) {
      prefetchModulePaymentsMap(mapped, setModulePaymentsMap, setLocalAccountDetails);
    }
  }, [accountData]);

  useEffect(() => {
    if (!courseList?.length) refetchCourseList?.();
    if (!uniData?.length) refetchUniData?.();
  }, []);

  const { data: verifications = [], refetch: refetchVerifications } = useQuery({
    queryKey: ["client-account-verifications", id],
    queryFn: async () => {
      const res = await axios.get(`/account-verification/client/${id}`);
      return res?.data?.data ?? [];
    },
    enabled: Boolean(id),
    refetchOnWindowFocus: true,
  });

  // Delete preview for a custom ("Others") entry — same shape the module pages render in DeleteModal.
  const deleteOtherPreviewQuery = useQuery({
    queryKey: ["other-entry-delete-preview", deleteOtherTarget.id],
    queryFn: () => fetchOtherEntryDeletePreview(deleteOtherTarget.id),
    enabled: Boolean(deleteOtherTarget.state && deleteOtherTarget.id),
    refetchOnWindowFocus: false,
  });

  const closeDeleteOtherModal = () => {
    setDeleteOtherTarget({ state: false, id: null });
    queryClient.removeQueries({ queryKey: ["other-entry-delete-preview"] });
  };

  const handleDeleteOtherEntry = () => {
    deleteData(
      `${API_URL}/accounts/other-entries/${deleteOtherTarget.id}`,
      () => {
        closeDeleteOtherModal();
        refetchAccountData?.();
      },
      localStorage.getItem("token")
    );
  };

  const verificationByDetailId = useMemo(() => {
    const map = new Map();
    // CAD-level verifications take priority
    verifications
      .filter((item) => !item.admissionDetailId && item.clientAccountDetailId)
      .forEach((item) => map.set(item.clientAccountDetailId, item));
    // For Admission-module CADs: fall back to the most recent admission-detail verification
    verifications
      .filter((item) => item.admissionDetailId && item.clientAccountDetailId)
      .forEach((item) => {
        if (!map.has(item.clientAccountDetailId)) {
          map.set(item.clientAccountDetailId, item);
        }
      });
    return map;
  }, [verifications]);

  const stagedVerifications = useMemo(
    () => verifications.filter((item) => item.requestType === "CREATE_ACCOUNT_ENTRY"),
    [verifications]
  );

  const groupedVerifications = useMemo(() => {
    const getGroupKey = (v) => {
      const details = v.submittedAccountDetail?.admissionDetails || [];
      if (details.length > 0) {
        const termKeys = details
          .map((d) => d.terms ?? d.termId ?? d.id)
          .filter(Boolean)
          .map(String)
          .sort()
          .join(",");
        if (termKeys) return `terms:${termKeys}`;
      }
      return v.clientAccountDetailId ? `cad:${v.clientAccountDetailId}` : `solo:${v.id}`;
    };
    const map = new Map();
    stagedVerifications.forEach((v) => {
      const key = getGroupKey(v);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    });
    return Array.from(map.values()).map((group) =>
      [...group].sort((a, b) => new Date(a.sentAt || 0) - new Date(b.sentAt || 0))
    );
  }, [stagedVerifications]);

  const verificationByAdmissionDetailId = useMemo(() => {
    const map = new Map();
    verifications
      .filter((item) => item.admissionDetailId)
      .forEach((item) => map.set(item.admissionDetailId, item));
    return map;
  }, [verifications]);

  const stagedAdmissionVerificationByTermKey = useMemo(() => {
    const map = new Map();
    verifications
      .filter(
        (item) =>
          item.requestType === "CREATE_ACCOUNT_ENTRY" &&
          item.submittedAccountDetail?.module === "Admission"
      )
      .forEach((item) => {
        const snapshot = item.submittedAccountDetail || {};
        const studentId = snapshot.admissionValues?.studentDbId;
        (snapshot.admissionDetails || []).forEach((row, index) => {
          const key = stagedAdmissionVerificationKey(studentId, row, index);
          if (key) {
            map.set(key, { verification: item, detail: row });
          }
        });
      });
    return map;
  }, [verifications]);

  const openVerificationEditor = (detail) => {
    const clone = JSON.parse(JSON.stringify(detail));
    setReceiptData({ receipts: [] });
    if (clone.module === "Admission") {
      setSelectedAdmissionDetailIds(
        (clone.admissionDetails || [])
          .filter((row) => {
            const verification = row?.id ? verificationByAdmissionDetailId.get(row.id) : null;
            return (
              row?.id &&
              toNumber(row.paidAmount) > 0 &&
              !["PENDING", "VERIFIED", "IMPORTED"].includes(verification?.status)
            );
          })
          .map((row) => row.id)
      );
    } else {
      setSelectedAdmissionDetailIds([]);
    }
    setVerificationEditor({ open: true, detail: clone });
  };

  const closeVerificationEditor = () => {
    setVerificationEditor({ open: false, detail: null });
    setReceiptData({ receipts: [] });
    setSelectedAdmissionDetailIds([]);
  };

  useEffect(() => {
    if (!clientAdmissions?.length) return;
    setTermGeneralInfoMap((prev) => {
      const next = { ...prev };
      clientAdmissions.forEach((student) => {
        const fresh = parseStudentAppData(student);
        if (!next[student.id]) {
          next[student.id] = buildTermGeneralInfoFromStudent(student);
        } else {
          // Refresh server-authoritative COE status without clobbering in-progress term-schedule edits.
          // COE is toggled on the application form (a different screen), never in this dialog, so it is
          // always safe to overwrite from the latest student data — keeps the "verification blocked" banner
          // and payment buttons in sync after the user marks COE received and returns here.
          next[student.id] = {
            ...next[student.id],
            coeReceived: fresh.coeReceived,
            coeApplied: fresh.coeApplied,
            coeReceivedDate: fresh.coeReceivedDate,
            coeReceivedNotes: fresh.coeReceivedNotes,
            applicationWithdrawn: fresh.applicationWithdrawn,
            applicationWithdrawnReason: fresh.applicationWithdrawnReason,
            applicationWithdrawnAt: fresh.applicationWithdrawnAt,
            applicationWithdrawnDocuments: fresh.applicationWithdrawnDocuments,
            statusBeforeWithdrawal: fresh.statusBeforeWithdrawal,
            // Term swap / offer-letter edits on other screens: sync schedule when Manage Admissions is closed.
            ...(manageAdmissionsOpen ? {} : pickTermScheduleServerFields(fresh)),
          };
        }
      });
      return next;
    });
  }, [clientAdmissions, manageAdmissionsOpen]);

  // Pull fresh student + account data when Manage Admissions opens (term swap, COE, application form).
  useEffect(() => {
    if (!manageAdmissionsOpen) return undefined;
    scheduleEditedSinceOpenRef.current = new Set();

    let cancelled = false;
    (async () => {
      try {
        const studentRefetch = await refetchClientAdmissions();
        const accountRefetch = await refetchAccountData();
        if (cancelled) return;

        const admissions = studentRefetch?.data ?? clientAdmissions ?? [];
        const details = accountRefetch?.data?.accountDetails ?? [];

        setTermGeneralInfoMap((prev) => {
          const next = { ...prev };
          admissions.forEach((student) => {
            // The user may have already started editing this admission's schedule while this
            // refresh was in flight — their local edits are authoritative now, don't clobber them.
            if (scheduleEditedSinceOpenRef.current.has(student.id)) return;
            const cad = details.find((d) => d.admissionValues?.studentDbId === student.id);
            next[student.id] = {
              ...buildTermGeneralInfoFromStudent(student, cad?.id ?? null),
              clientAccountDetailId: cad?.id ?? prev[student.id]?.clientAccountDetailId,
            };
          });
          return next;
        });

        await Promise.all(
          details
            .filter((row) => row.module === "Admission" && row.id)
            .map((row) => queryClient.invalidateQueries({ queryKey: ["admissionTerms", row.id] }))
        );
        await refetchVerifications();
      } catch (err) {
        console.error("Failed to refresh Manage Admissions data", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageAdmissionsOpen]);

  const lockedTermIdsForAdmission = useCallback(
    (studentId) => {
      const locked = new Set();
      (verifications || [])
        .filter(
          (v) =>
            ["PENDING", "VERIFIED", "IMPORTED"].includes(v.status) &&
            v.submittedAccountDetail?.admissionValues?.studentDbId === studentId
        )
        .forEach((v) => {
          (v.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
            if (d.terms) locked.add(String(d.terms).replace(/^[Tt]/, ""));
          });
        });
      return locked;
    },
    [verifications]
  );

  const rejectedTermIndicesForAdmission = useCallback(
    (studentId) => {
      const all = (verifications || []).filter(
        (v) => v.submittedAccountDetail?.admissionValues?.studentDbId === studentId
      );
      const importedOrVerified = new Set();
      all.forEach((v) => {
        if (v.status === "IMPORTED" || v.status === "VERIFIED") {
          (v.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
            const n = parseInt(
              String(d.terms || "")
                .trim()
                .replace(/^[Tt]/, ""),
              10
            );
            if (!Number.isNaN(n) && n > 0) importedOrVerified.add(n - 1);
          });
        }
      });
      const rejected = new Set();
      all.forEach((v) => {
        if (v.status === "REJECTED") {
          (v.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
            const n = parseInt(
              String(d.terms || "")
                .trim()
                .replace(/^[Tt]/, ""),
              10
            );
            if (!Number.isNaN(n) && n > 0 && !importedOrVerified.has(n - 1)) rejected.add(n - 1);
          });
        }
      });
      return rejected;
    },
    [verifications]
  );

  const refreshAdmissionAccountsView = useCallback(
    async ({ studentId, cadId } = {}) => {
      try {
        const [studentRefetch, accountResult] = await Promise.all([
          refetchClientAdmissions(),
          refetchAccountData(),
        ]);
        const students = studentRefetch?.data ?? clientAdmissions ?? [];
        if (studentId) {
          const student = students.find((s) => Number(s.id) === Number(studentId));
          if (student) {
            setTermGeneralInfoMap((prev) => ({
              ...prev,
              [studentId]: {
                ...buildTermGeneralInfoFromStudent(
                  student,
                  cadId ?? prev[studentId]?.clientAccountDetailId
                ),
              },
            }));
          }
        }

        const details = accountResult?.data?.accountDetails ?? [];
        const admissionRows = details.filter((row) => row.module === "Admission" && row.id);
        const rowsForTotals = cadId
          ? admissionRows.filter((row) => row.id === cadId)
          : admissionRows;

        await Promise.all(
          rowsForTotals.map((row) =>
            queryClient.invalidateQueries({ queryKey: ["admissionTerms", row.id] })
          )
        );

        const updates = {};
        await Promise.all(
          rowsForTotals.map(async (row) => {
            try {
              const terms = await queryClient.fetchQuery({
                queryKey: ["admissionTerms", row.id],
                queryFn: () => fetchAdmissionTerms(row.id),
              });
              updates[row.id] = computeAdmissionTermTotalsFromTerms(
                terms ?? [],
                row.admissionDetails
              );
            } catch {
              updates[row.id] = computeAdmissionTermTotalsFromTerms([], row.admissionDetails);
            }
          })
        );
        setAdmissionTermTotalsMap((prev) => ({ ...prev, ...updates }));
        await queryClient.invalidateQueries({ queryKey: ["client-summary", id] });
      } catch (err) {
        console.error("Failed to refresh accounts after term schedule save", err);
      }
    },
    [id, queryClient, refetchAccountData, refetchClientAdmissions, clientAdmissions]
  );

  const handleApplicationWithdrawn = useCallback(
    (studentId, fields) => {
      setTermGeneralInfoMap((prev) => ({
        ...prev,
        [studentId]: { ...prev[studentId], ...fields },
      }));
      refetchClientAdmissions();
      refreshAdmissionAccountsView({ studentId });
    },
    [refetchClientAdmissions, refreshAdmissionAccountsView]
  );

  const handleApplicationReactivated = useCallback(
    (studentId) => {
      setTermGeneralInfoMap((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          applicationWithdrawn: false,
        },
      }));
      refetchClientAdmissions();
      refreshAdmissionAccountsView({ studentId });
    },
    [refetchClientAdmissions, refreshAdmissionAccountsView]
  );

  const relockSavedTermSchedule = (studentId) =>
    setTermGeneralInfoMap((prev) =>
      prev[studentId] ? { ...prev, [studentId]: relockSavedSchedule(prev[studentId]) } : prev
    );

  const handleSaveTermSchedule = async (studentId) => {
    const generalInfo = termGeneralInfoMap[studentId];
    if (!generalInfo) return false;
    const student = clientAdmissions.find((s) => Number(s.id) === Number(studentId));
    const payload = buildOfferReceivedPatchPayload(generalInfo, student);
    setTermSavingMap((prev) => ({ ...prev, [studentId]: true }));
    try {
      const res = await axios.patch(`/student/${studentId}/application/offer-received`, payload);
      relockSavedTermSchedule(studentId);
      const cadId = res.data?.clientAccountDetailId ?? res.data?.data?.clientAccountDetailId;
      if (cadId) {
        setTermGeneralInfoMap((prev) => ({
          ...prev,
          [studentId]: { ...prev[studentId], clientAccountDetailId: cadId },
        }));
      }
      const resolvedCadId =
        cadId ??
        generalInfo.clientAccountDetailId ??
        effectiveAccountDetails.find((d) => d.admissionValues?.studentDbId === studentId)?.id;
      if (resolvedCadId) {
        await queryClient.refetchQueries({ queryKey: ["admissionTerms", resolvedCadId] });
      }
      toast.success("Term schedule saved");
      void refreshAdmissionAccountsView({ studentId, cadId: resolvedCadId });
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save term schedule"));
      return false;
    } finally {
      setTermSavingMap((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const saveTermScheduleForTask = async (studentId) => {
    const info = termGeneralInfoMap[studentId];
    if (!info) return false;
    const student = clientAdmissions.find((s) => Number(s.id) === Number(studentId));
    const payload = buildOfferReceivedPatchPayload(info, student);
    try {
      await axios.patch(`/student/${studentId}/application/offer-received`, payload);
      relockSavedTermSchedule(studentId);
      return true;
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, "Failed to save term schedule. Save manually and retry.")
      );
      return false;
    }
  };

  const openAccountEditor = () => {
    navigate(`/admin/account/add`, {
      state: {
        item: {
          ...accountData,
          hasAccountDetails: Boolean(accountData?.id),
          clientData,
          clientAccountId: accountData?.id || null,
        },
      },
    });
  };

  const buildStagedAdmissionDetail = (student) => {
    const admissionValues = buildAdmissionValuesFromStudent({
      student,
      courseList,
      uniData,
    });
    const termSchedule = getAdmissionTermSchedule({ student, admissionValues });
    if (!termSchedule.length) {
      return null;
    }
    const parentId = crypto.randomUUID();
    const admissionDetails = buildAdmissionInstallmentsFromTermSchedule(
      parentId,
      termSchedule,
      [],
      admissionValues
    );
    return calculateRowValues({
      ...createNewAccountDetail(),
      uuid: parentId,
      module: "Admission",
      admissionValues: { ...admissionValues, termSchedule },
      admissionDetails,
    });
  };

  const fetchFreshAdmission = async (studentId, fallbackStudent) => {
    try {
      const res = await axios.get(`/student/${studentId}`);
      return res?.data?.data ?? res?.data ?? fallbackStudent;
    } catch {
      return fallbackStudent;
    }
  };

  const openStagedEditor = async () => {
    setStagedReceiptData({ receipts: [] });
    setSelectedStagedAdmissionRowKeys([]);
    setStagedAdmissionLoading(true);
    setStagedEditor({
      open: true,
      detail: calculateRowValues({
        ...createNewAccountDetail(),
        module: "Admission",
        admissionDetails: [],
      }),
    });

    // Fetch fresh admissions so newly created ones appear
    let freshAdmissions = clientAdmissions;
    try {
      const result = await refetchClientAdmissions();
      if (Array.isArray(result?.data)) {
        freshAdmissions = result.data;
      }
    } catch {
      // fall back to stale list
    }
    setAdmissionsForStagedEditor(freshAdmissions);

    if (freshAdmissions.length === 1) {
      const student = freshAdmissions[0];
      const freshStudent = await fetchFreshAdmission(student.id, student);
      const detail = buildStagedAdmissionDetail(freshStudent);
      setStagedAdmissionLoading(false);
      if (detail) {
        setStagedEditor({ open: true, detail });
      }
    } else {
      setStagedAdmissionLoading(false);
    }
  };

  const closeStagedEditor = () => {
    setStagedEditor({ open: false, detail: null });
    setStagedReceiptData({ receipts: [] });
    setSelectedStagedAdmissionRowKeys([]);
    setAdmissionsForStagedEditor([]);
  };

  const updateStagedDetail = (patch) => {
    setStagedEditor((prev) => ({
      ...prev,
      detail: calculateRowValues({ ...prev.detail, ...patch }),
    }));
  };

  const setStagedModule = (module) => {
    if (module === "Admission") {
      setSelectedStagedAdmissionRowKeys([]);
      if (admissionsForStagedEditor.length === 1) {
        setStagedAdmission(admissionsForStagedEditor[0].id);
      } else {
        setStagedEditor((prev) => ({
          ...prev,
          detail: calculateRowValues({
            ...createNewAccountDetail(),
            module: "Admission",
            admissionDetails: [],
          }),
        }));
      }
      return;
    }
    setSelectedStagedAdmissionRowKeys([]);
    setStagedEditor((prev) => ({
      ...prev,
      detail: calculateRowValues({
        ...createNewAccountDetail(),
        module,
      }),
    }));
  };

  const setStagedAdmission = async (studentId) => {
    const student = admissionsForStagedEditor.find((item) => Number(item.id) === Number(studentId));
    if (!student) return;
    setStagedAdmissionLoading(true);
    const freshStudent = await fetchFreshAdmission(studentId, student);
    const detail = buildStagedAdmissionDetail(freshStudent);
    setStagedAdmissionLoading(false);
    if (!detail) {
      toast.error(ADMISSION_TERM_SCHEDULE_REQUIRED_MESSAGE);
      return;
    }
    setSelectedStagedAdmissionRowKeys([]);
    setStagedEditor((prev) => ({
      ...prev,
      detail,
    }));
  };

  const openOfferLetterTermSchedule = () => {
    const admissionId =
      stagedEditor.detail?.admissionValues?.studentDbId || admissionsForStagedEditor[0]?.id;
    if (!admissionId) {
      toast.info("Select an admission first.");
      return;
    }
    navigate(`/admin/admission/edit/${admissionId}`, {
      state: { tab: 2, applicationPhase: 3 },
    });
  };

  const saveToTermSchedule = async () => {
    const studentDbId = stagedEditor.detail?.admissionValues?.studentDbId;
    if (!studentDbId) {
      toast.warning("Select an admission first.");
      return;
    }
    setStagedAdmissionLoading(true);
    try {
      const freshStudent = await fetchFreshAdmission(studentDbId, null);
      if (!freshStudent) throw new Error("Could not fetch student");
      const appData = parseAdmissionApplicationData(freshStudent.applicationData);
      const termSchedule = Array.isArray(appData.termSchedule) ? appData.termSchedule : [];
      const admissionDetails = stagedEditor.detail.admissionDetails || [];

      const updatedTermSchedule = termSchedule.map((term, i) => {
        const row =
          admissionDetails.find((d) => d.termId && d.termId === term.id) || admissionDetails[i];
        if (!row) return term;
        return { ...term, paidAmount: row.paidAmount ?? term.paidAmount };
      });

      const courseStartDate = appData.courseStartDate || freshStudent.courseStartDate;
      const courseEndDate = appData.courseEndDate || freshStudent.courseEndDate;
      const studentId = appData.studentId || freshStudent.studentId;

      await axios.patch(`/student/${studentDbId}/application/offer-received`, {
        termSchedule: updatedTermSchedule,
        courseStartDate,
        courseEndDate,
        studentId,
      });
      toast.success("Paid amounts saved to term schedule.");
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setStagedAdmissionLoading(false);
    }
  };

  const updateStagedAdmissionRow = (index, patch) => {
    setStagedEditor((prev) => {
      const rows = [...(prev.detail?.admissionDetails || [])];
      rows[index] = { ...rows[index], ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "paidAmount")) {
        const key = stagedAdmissionRowKey(rows[index], index);
        const paid = toNumber(patch.paidAmount);
        const fee = toNumber(rows[index].perSemFee);
        const material = toNumber(rows[index].materialFee);
        const discount = toNumber(rows[index].discount);
        const agreed = fee + material - discount;
        const meetsMinimum = agreed <= 0 || paid >= agreed;
        setSelectedStagedAdmissionRowKeys((current) => {
          if (paid > 0 && meetsMinimum && !current.includes(key)) {
            return [...current, key];
          }
          if (paid <= 0 || !meetsMinimum) {
            return current.filter((item) => item !== key);
          }
          return current;
        });
      }
      return {
        ...prev,
        detail: calculateRowValues({ ...prev.detail, admissionDetails: rows }),
      };
    });
  };

  const toggleStagedAdmissionRow = (row, index) => {
    const key = stagedAdmissionRowKey(row, index);
    setSelectedStagedAdmissionRowKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  };

  const stagedAccountDetailsForSend = useMemo(() => {
    if (!stagedEditor.detail) return [];
    if (stagedEditor.detail.module !== "Admission") {
      return [buildVerificationSnapshot(stagedEditor.detail)];
    }
    const studentId = stagedEditor.detail.admissionValues?.studentDbId;
    const selectedRows = (stagedEditor.detail.admissionDetails || []).filter((row, index) => {
      const rowKey = stagedAdmissionRowKey(row, index);
      const verificationKey = stagedAdmissionVerificationKey(studentId, row, index);
      const existing = verificationKey
        ? stagedAdmissionVerificationByTermKey.get(verificationKey)
        : null;
      return (
        selectedStagedAdmissionRowKeys.includes(rowKey) &&
        !isActiveStagedVerification(existing?.verification?.status)
      );
    });
    if (selectedRows.length === 0) return [];
    return [
      buildVerificationSnapshot({
        ...stagedEditor.detail,
        admissionDetails: selectedRows,
      }),
    ];
  }, [selectedStagedAdmissionRowKeys, stagedAdmissionVerificationByTermKey, stagedEditor.detail]);

  const updateDraft = (patch) => {
    setVerificationEditor((prev) => ({ ...prev, detail: { ...prev.detail, ...patch } }));
  };

  const updateAdmissionDraft = (index, patch) => {
    setVerificationEditor((prev) => {
      const rows = [...(prev.detail?.admissionDetails || [])];
      rows[index] = recalculateAdmissionDetail({ ...rows[index], ...patch });
      return {
        ...prev,
        detail: buildVerificationSnapshot({ ...prev.detail, admissionDetails: rows }),
      };
    });
  };

  const addAdmissionDraftRow = () => {
    setVerificationEditor((prev) => {
      const rows = [...(prev.detail?.admissionDetails || [])];
      rows.push(
        recalculateAdmissionDetail({
          uuid: `draft-${Date.now()}`,
          date: "",
          terms: String(rows.length + 1),
          perSemFee: "0",
          perSemCost: "0",
          materialFee: "0",
          paidAmount: "0",
          dueAmount: "0",
          referral: "0",
          discount: "0",
        })
      );
      return {
        ...prev,
        detail: buildVerificationSnapshot({ ...prev.detail, admissionDetails: rows }),
      };
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "module",
        header: "Module",
        size: 200,
        Cell: ({ cell, row }) => {
          const subtitle = getAccountRowSubtitle(row.original);
          return (
            <div className="flex flex-col items-start gap-1">
              <Chip label={cell.getValue()} color="primary" variant="outlined" size="small" />
              {subtitle && (
                <Typography
                  variant="caption"
                  className="text-app-text-secondary"
                  title={subtitle}
                  sx={{
                    lineHeight: 1.3,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    display: "block",
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "agreedAmount",
        header: "Amount",
        size: 100,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },
      {
        accessorKey: "discount",
        header: "Discount",
        size: 100,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },
      {
        accessorKey: "paidAmount",
        header: "Paid Amount",
        size: 100,
        Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
      },
      {
        accessorKey: "dueAmount",
        header: "Due Amount",
        size: 100,
        Cell: ({ cell }) => formatMoney(Math.max(0, toNumber(cell.getValue()))),
      },
      ...(canViewAgencyFinancials()
        ? [
            {
              accessorKey: "referral",
              header: "Referral",
              size: 120,
              Cell: ({ cell }) => formatMoney(cell.getValue() || 0),
            },
          ]
        : []),
      {
        id: "rowNotes",
        header: "Notes",
        size: 80,
        Cell: ({ row }) => {
          const count = row.original.notes?.length || 0;
          const rowUuid = row.original.uuid || (row.original.id ? `row-${row.original.id}` : null);
          return (
            <Tooltip
              title={
                !canViewAccountNotes
                  ? "You need the Accounts permission to view account notes"
                  : count
                  ? `${count} note(s)`
                  : "Add note"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!canViewAccountNotes}
                  onClick={() =>
                    setOpenNotesModal({
                      state: true,
                      data: { ...row.original, uuid: rowUuid },
                      subtable: false,
                      parentId: null,
                    })
                  }
                >
                  <Badge
                    badgeContent={canViewAccountNotes ? count || 0 : 0}
                    color="error"
                    max={99}
                    invisible={!canViewAccountNotes}
                    sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}
                  >
                    <EventNoteOutlined
                      fontSize="small"
                      className={canViewAccountNotes ? "text-blue-500" : "text-gray-300"}
                    />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: "modulePayments",
        header: "Payments",
        size: 80,
        Cell: ({ row }) => {
          if (row.original.module === "Admission") return null;
          if (MODULES_WITH_DEDICATED_PAYMENT_FLOW.includes(row.original.module)) return null;
          const cadId = row.original.id;
          const count = (modulePaymentsMap[cadId] || []).length;
          // A custom entry is linked to the Others module by its name, so a nameless row has no
          // module record to send a payment to. Its history still shows; adding to it does not.
          const nameless = !String(row.original.module || "").trim();
          return (
            <Tooltip title={nameless ? "View payments" : "View / add payments"}>
              <IconButton
                size="small"
                onClick={() =>
                  hydrateModulePaymentsForRow(
                    row.original,
                    setModulePaymentsMap,
                    setLocalAccountDetails
                  ).then(() => setPaymentsModal({ open: true, row: row.original }))
                }
              >
                <Badge
                  badgeContent={count || 0}
                  color="primary"
                  max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}
                >
                  <MonetizationOnOutlinedIcon fontSize="small" className="text-blue-500" />
                </Badge>
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [modulePaymentsMap]
  );

  const effectiveAccountDetails =
    localAccountDetails.length > 0
      ? localAccountDetails
      : accountData?.accountDetails?.length > 0
      ? accountData?.accountDetails
      : [];

  // Admission rows appear once COE is received, any payment is verified, or a payment awaits verification.
  // effectiveAccountDetails keeps hidden rows so Manage Admissions can still edit the term schedule before COE.
  const visibleAccountDetails = useMemo(
    () => effectiveAccountDetails.filter((d) => shouldShowAdmissionInAccounts(d)),
    [effectiveAccountDetails]
  );

  useEffect(() => {
    const admissionRows = effectiveAccountDetails.filter(
      (row) => row.module === "Admission" && row.id
    );
    if (!admissionRows.length) return undefined;

    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        admissionRows.map(async (row) => {
          try {
            const res = await axios.get(`${API_URL}/admission-terms/admission-detail/${row.id}`);
            if (cancelled) return;
            const terms = res.data?.data ?? [];
            updates[row.id] = computeAdmissionTermTotalsFromTerms(terms, row.admissionDetails);
          } catch {
            if (!cancelled) {
              updates[row.id] = computeAdmissionTermTotalsFromTerms([], row.admissionDetails);
            }
          }
        })
      );
      if (!cancelled) {
        setAdmissionTermTotalsMap((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveAccountDetails]);

  const handleTermTotalsChange = useCallback(
    (cadId, totals) => setAdmissionTermTotalsMap((prev) => ({ ...prev, [cadId]: totals })),
    []
  );

  const handleOpenNotesModal = useCallback(
    (payload) => setOpenNotesModal(payload),
    [setOpenNotesModal]
  );

  // A parent Admission row's stored Amount / Discount / Paid / Due / Referral reflect only its
  // IMPORTED installments. Override them with the full-term totals (imported + un-imported) so the
  // collapsed row matches the sum of its expanded term rows and the Financial Snapshot. Falls back
  // to the stored values until that admission's term totals have been fetched.
  const tableData = useMemo(
    () =>
      visibleAccountDetails.map((row) => {
        if (row.module !== "Admission") return row;
        const termTotals = admissionTermTotalsMap[row.id];
        if (!termTotals) return row;
        return {
          ...row,
          agreedAmount: termTotals.totalAgreed,
          discount: termTotals.totalDiscount,
          paidAmount: termTotals.totalPaid,
          dueAmount: termTotals.totalDue,
          referral: termTotals.totalReferral,
        };
      }),
    [visibleAccountDetails, admissionTermTotalsMap]
  );

  const table = useMaterialReactTable({
    columns,
    enableRowNumbers: true,
    data: tableData,
    enableRowActions: true,

    renderRowActionMenuItems: ({ row }) => {
      const items = [
        <MenuItem
          key="edit"
          onClick={() =>
            hasPermission(P.ACCOUNTS.READ)
              ? navigate(`/admin/account/add`, {
                  state: {
                    item: {
                      ...accountData,
                      hasAccountDetails: true,
                      clientData: clientData,
                      clientAccountId: accountData?.id,
                    },
                  },
                })
              : toast.error("You are not authorized to edit account")
          }
        >
          Edit
        </MenuItem>,
        // <MenuItem
        //   key="send-verification"
        //   disabled={
        //     !hasPermission(P.ACCOUNTS.READ) ||
        //     ["PENDING", "VERIFIED", "IMPORTED"].includes(
        //       verificationByDetailId.get(row.original.id)?.status
        //     )
        //   }
        //   onClick={() =>
        //     hasPermission(P.ACCOUNTS.READ)
        //       ? openVerificationEditor(row.original)
        //       : toast.error("You are not authorized to edit account")
        //   }
        // >
        //   Send for verification
        // </MenuItem>,
      ];
      // A custom entry has no module page of its own, so it is edited and deleted from here —
      // every other linked row is managed from its module's screen instead.
      if (isOtherEntryRow(row.original)) {
        if (canEditOtherEntry) {
          items.push(
            <MenuItem key="edit-entry" onClick={() => setEditOtherEntryRow(row.original)}>
              Edit entry
            </MenuItem>
          );
        }
        if (canDeleteOtherEntry) {
          items.push(
            <MenuItem
              key="delete-entry"
              sx={{ color: "error.main" }}
              onClick={() => setDeleteOtherTarget({ state: true, id: row.original.id })}
            >
              Delete entry
            </MenuItem>
          );
        }
      }
      return items;
    },
    renderDetailPanel: ({ row }) => {
      if (row.original.module !== "Admission") return null;
      const parentUuid = row.original.uuid || (row.original.id ? `row-${row.original.id}` : null);
      return (
        <SecondRow
          accountDetails={row.original.admissionDetails}
          parentUuid={parentUuid}
          admissionId={row.original.admissionValues?.studentDbId}
          clientId={clientData?.id}
          clientAccountDetailId={row.original.id}
          module={row.original.module}
          onTermTotalsChange={handleTermTotalsChange}
          onOpenNotesModal={handleOpenNotesModal}
        />
      );
    },
    muiExpandButtonProps: ({ row }) => ({
      sx: { display: row.original.module === "Admission" ? "flex" : "none" },
    }),
    positionActionsColumn: "last",

    initialState: {
      enableGlobalFilter: false,
      showGlobalFilter: false,
      density: "compact",
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },

    enablePagination: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "400px",
        overflowY: "auto",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
    state: {
      isLoading: isLoading,
      showProgressBars: isLoading,
    },
  });

  return (
    <div className="bg-app-surface rounded-lg shadow-sm">
      <AddOtherEntryDialog
        open={addOtherEntryOpen}
        clientId={id}
        onClose={() => setAddOtherEntryOpen(false)}
        onCreated={() => refetchAccountData?.()}
      />
      <AddOtherEntryDialog
        open={Boolean(editOtherEntryRow)}
        clientId={id}
        initialEntry={editOtherEntryRow}
        onClose={() => setEditOtherEntryRow(null)}
        onCreated={() => refetchAccountData?.()}
      />
      {deleteOtherTarget.state && (
        <DeleteModal
          open={deleteOtherTarget.state}
          item="custom entry"
          handleCancel={closeDeleteOtherModal}
          handleDelete={handleDeleteOtherEntry}
          preview={deleteOtherPreviewQuery.data ?? null}
          previewLoading={deleteOtherPreviewQuery.isFetching}
        />
      )}
      <div className="flex justify-end gap-2 p-3">
        {clientAdmissions?.some((s) => termGeneralInfoMap[s.id]?.termSchedule?.length) && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setActiveAdmissionTab(0);
              setManageAdmissionsOpen(true);
            }}
          >
            Manage Admissions
          </Button>
        )}
        {hasPermission(P.ACCOUNTS.READ) && (
          <Button variant="outlined" onClick={openAccountEditor}>
            {accountData?.id ? "Open Account Editor" : "Add Direct Account"}
          </Button>
        )}
        {hasPermission([P.ACCOUNTS.OTHER_ENTRY_MANAGE, P.ACCOUNTS.CREATE]) && (
          <Button variant="contained" onClick={() => setAddOtherEntryOpen(true)}>
            Add custom entry
          </Button>
        )}
        {/*{canSendClientAccountVerification && (*/}
        {/*  <Button variant="contained" onClick={openStagedEditor}>*/}
        {/*    Add account for verification*/}
        {/*  </Button>*/}
        {/*)}}*/}
      </div>
      {groupedVerifications.length > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={false}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "background.default", borderRadius: "8px" }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Admission Verification history
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  ({groupedVerifications.length}{" "}
                  {groupedVerifications.length === 1 ? "entry" : "entries"})
                </Typography>
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1.5 }}>
              <Stack spacing={0.5}>
                {groupedVerifications.map((group) => {
                  const firstV = group[0];
                  const lastV = group[group.length - 1];
                  const snapshot = lastV.submittedAccountDetail || {};
                  const admissionDetails = snapshot.admissionDetails || [];
                  const totalPaid =
                    admissionDetails.length > 0
                      ? admissionDetails.reduce((sum, r) => sum + toNumber(r.paidAmount), 0)
                      : toNumber(snapshot.paidAmount);
                  const totalAgreed =
                    admissionDetails.length > 0
                      ? admissionDetails.reduce(
                          (sum, r) =>
                            sum +
                            toNumber(
                              r.agreed ??
                                toNumber(r.perSemFee) +
                                  toNumber(r.materialFee) -
                                  toNumber(r.discount)
                            ),
                          0
                        )
                      : toNumber(snapshot.agreedAmount);

                  const statusColor =
                    {
                      PENDING: "warning",
                      VERIFIED: "success",
                      REJECTED: "error",
                      IMPORTED: "primary",
                    }[lastV.status] || "default";

                  const fmtDate = (d) => (d ? formatDisplayDateTime(d) : null);

                  const timelineEvents = [];
                  group.forEach((v, idx) => {
                    if (v.sentAt) {
                      timelineEvents.push({
                        label: idx === 0 ? "Sent for verification" : "Resubmitted",
                        actor: v.sentByName,
                        date: fmtDate(v.sentAt),
                        color: idx === 0 ? "text.secondary" : "info.main",
                      });
                    }
                    if (v.rejectedAt) {
                      timelineEvents.push({
                        label: "Rejected",
                        actor: v.rejectedByName,
                        date: fmtDate(v.rejectedAt),
                        note: v.rejectionFeedback,
                        color: "error.main",
                      });
                    }
                    if (v.verifiedAt) {
                      timelineEvents.push({
                        label: "Verified",
                        actor: v.verifiedByName,
                        date: fmtDate(v.verifiedAt),
                        color: "success.main",
                      });
                    }
                    if (v.importedAt) {
                      timelineEvents.push({
                        label: "Imported to account",
                        actor: v.importedByName,
                        date: fmtDate(v.importedAt),
                        color: "primary.main",
                      });
                    }
                    (v.auditLogs || []).forEach((log) => {
                      if (
                        ![
                          "SENT",
                          "REJECTED",
                          "VERIFIED",
                          "IMPORTED",
                          "VERIFIED_AND_IMPORTED",
                        ].includes(log.action)
                      ) {
                        timelineEvents.push({
                          label: log.action,
                          actor: log.actorName,
                          date: fmtDate(log.timestamp),
                          note: log.details,
                          color: "text.secondary",
                        });
                      }
                    });
                  });

                  return (
                    <Accordion
                      key={firstV.clientAccountDetailId || firstV.id}
                      disableGutters
                      elevation={0}
                      defaultExpanded={false}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "6px !important",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": { borderColor: "primary.light" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          minHeight: 40,
                          py: 0.5,
                          px: 1.5,
                          "& .MuiAccordionSummary-content": { my: 0.5 },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ flex: 1, mr: 1 }}
                        >
                          <Chip
                            label={firstV.module || "Account"}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={lastV.status}
                            color={statusColor}
                            size="small"
                            sx={{ fontSize: 11, height: 20 }}
                          />
                          {group.length > 1 && (
                            <Chip
                              label={`${group.length} submissions`}
                              variant="outlined"
                              size="small"
                              sx={{ fontSize: 11, height: 20 }}
                            />
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            Sent by {firstV.sentByName || "Unknown"}
                            {firstV.sentAt ? ` · ${formatDisplayDate(firstV.sentAt)}` : ""}
                          </Typography>
                          {totalPaid > 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: 12 }}
                            >
                              · Paid: <strong>{formatMoney(totalPaid)}</strong>
                              {totalAgreed > 0 && ` / ${formatMoney(totalAgreed)}`}
                            </Typography>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                        <Stack spacing={1}>
                          {timelineEvents.map((ev, i) => (
                            <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: ev.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography variant="caption" color={ev.color} fontWeight={600}>
                                  {ev.label}
                                </Typography>
                                {ev.actor && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    by {ev.actor}
                                  </Typography>
                                )}
                                {ev.date && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ ml: 0.5 }}
                                  >
                                    · {ev.date}
                                  </Typography>
                                )}
                                {ev.note && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.25, fontSize: 12, fontStyle: "italic" }}
                                  >
                                    "{ev.note}"
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                          {admissionDetails.length > 0 && (
                            <Box
                              sx={{
                                mt: 0.5,
                                pt: 1,
                                borderTop: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                display="block"
                                sx={{ mb: 0.5 }}
                              >
                                Submitted terms ({admissionDetails.length})
                              </Typography>
                              <Stack spacing={0.25}>
                                {admissionDetails.map((row, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: 11 }}
                                  >
                                    Term {row.terms || i + 1}
                                    {row.perSemFee ? ` · Fee: ${formatMoney(row.perSemFee)}` : ""}
                                    {row.paidAmount
                                      ? ` · Paid: ${formatMoney(row.paidAmount)}`
                                      : ""}
                                    {row.date ? ` · Date: ${row.date}` : ""}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      {visibleAccountDetails.length > 0 &&
        (() => {
          const enrichedDetails = visibleAccountDetails.map((row) => {
            if (row.module === "Admission") {
              const termTotals = admissionTermTotalsMap[row.id];
              if (termTotals) {
                return {
                  ...row,
                  paidAmount: String(termTotals.totalPaid),
                  dueAmount: String(termTotals.totalDue),
                };
              }
              return buildVerificationSnapshot(row);
            }
            // Non-Admission: recompute due from current paid (module payments are always verified)
            const agreed = toNumber(row.agreedAmount);
            const discount = toNumber(row.discount);
            const paid = toNumber(row.paidAmount);
            return { ...row, dueAmount: String(Math.max(0, agreed - discount - paid)) };
          });
          const totals = calculateTotals(enrichedDetails);
          const effectiveDue = enrichedDetails.reduce((sum, row) => {
            if (row.module === "Admission") {
              const termTotals = admissionTermTotalsMap[row.id];
              return sum + (termTotals?.totalEffectiveDue ?? 0);
            }
            return sum + Math.max(0, toNumber(row.dueAmount));
          }, 0);
          const totalDue = Math.max(0, totals.totalDue);
          const fmt = (n) =>
            Number(n || 0).toLocaleString("en-AU", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          return (
            <div className="mx-4 mb-4 rounded-xl border border-app-border bg-app-surface p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-app-text">Financial Snapshot</h3>
                <span className="text-xs text-app-text-muted">
                  Live totals from all account entries
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-app-border bg-app-surface-muted px-4 py-3 flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
                    Total Paid
                  </span>
                  <span className="text-lg font-semibold text-app-text">
                    $ {fmt(totals.totalPaid)}
                  </span>
                </div>
                <div
                  className={`rounded-xl border px-4 py-3 flex flex-col gap-1 ${
                    effectiveDue > 0
                      ? "border-amber-300 bg-amber-50"
                      : "border-app-border bg-app-surface-muted"
                  }`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-app-text-muted flex items-center gap-1">
                    {effectiveDue > 0 && (
                      <WarningAmber sx={{ fontSize: 14 }} className="text-amber-500" />
                    )}
                    Effective Due
                  </span>
                  <span
                    className={`text-lg font-semibold ${
                      effectiveDue > 0 ? "text-amber-700" : "text-app-text"
                    }`}
                  >
                    $ {fmt(effectiveDue)}
                  </span>
                  <span className="text-xs text-app-text-muted">currently collectable</span>
                </div>
                <div
                  className={`rounded-xl border px-4 py-3 flex flex-col gap-1 ${
                    totalDue > 0
                      ? "border-orange-200 bg-orange-50"
                      : "border-app-border bg-app-surface-muted"
                  }`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
                    Total Due
                  </span>
                  <span
                    className={`text-lg font-semibold ${
                      totalDue > 0 ? "text-orange-700" : "text-app-text"
                    }`}
                  >
                    $ {fmt(totalDue)}
                  </span>
                  <span className="text-xs text-app-text-muted">incl. future terms</span>
                </div>
              </div>
            </div>
          );
        })()}
      <MaterialReactTable table={table} />
      {/* Manage Admissions Dialog — term schedules editable here, saves to Student.applicationData */}
      {(() => {
        const admissionsWithTerms = (clientAdmissions || []).filter(
          (s) => termGeneralInfoMap[s.id]?.termSchedule?.length
        );
        if (!admissionsWithTerms.length) return null;
        const activeStudent = admissionsWithTerms[activeAdmissionTab] || admissionsWithTerms[0];
        const baseGeneralInfo = termGeneralInfoMap[activeStudent?.id] || {};
        const activeCad = effectiveAccountDetails.find(
          (d) => d.admissionValues?.studentDbId === activeStudent?.id
        );
        const generalInfo = activeCad?.id
          ? { ...baseGeneralInfo, clientAccountDetailId: activeCad.id }
          : baseGeneralInfo;
        const lockedIds = lockedTermIdsForAdmission(activeStudent?.id);
        const rejectedIndices = rejectedTermIndicesForAdmission(activeStudent?.id);
        const isSaving = termSavingMap[activeStudent?.id] || false;
        const activeAppData = {
          ...parseStudentAppData(activeStudent),
          ...(termGeneralInfoMap[activeStudent?.id] || {}),
        };
        const applicationWithdrawn = Boolean(activeAppData.applicationWithdrawn);

        return (
          <Dialog
            open={manageAdmissionsOpen}
            onClose={() => setManageAdmissionsOpen(false)}
            maxWidth="xl"
            fullWidth
            PaperProps={{ sx: { height: "90vh" } }}
          >
            <DialogTitle
              sx={{
                pb: 0,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <div>
                <Typography variant="h6" fontWeight={600}>
                  Manage Admissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit term schedules below. Changes save to the admission offer letter.
                </Typography>
                <Chip
                  size="small"
                  label={
                    generalInfo?.coeReceived
                      ? "COE received"
                      : "COE not received — new payments blocked"
                  }
                  color={generalInfo?.coeReceived ? "success" : "warning"}
                  variant={generalInfo?.coeReceived ? "outlined" : "filled"}
                  sx={{ mt: 1 }}
                />
              </div>
              {activeStudent?.id && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexShrink: 0 }}>
                  {!applicationWithdrawn && (
                    <ApplicationWithdrawPanel
                      admissionId={activeStudent.id}
                      withdrawn={false}
                      inline
                      onWithdrawn={(fields) => handleApplicationWithdrawn(activeStudent.id, fields)}
                    />
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setManageAdmissionsOpen(false);
                      navigate(`/admin/admission/edit/${activeStudent.id}`, {
                        state: { tab: 2, applicationPhase: 3 },
                      });
                    }}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Open Application Form
                  </Button>
                </Stack>
              )}
            </DialogTitle>
            <Divider sx={{ mt: 1 }} />
            {admissionsWithTerms.length > 1 && (
              <Tabs
                value={Math.min(activeAdmissionTab, admissionsWithTerms.length - 1)}
                onChange={(_, v) => setActiveAdmissionTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 2,
                  pt: 0.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  minHeight: 48,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: 44,
                    py: 1.25,
                    px: 2,
                    mr: 0.5,
                    fontWeight: 500,
                    color: "text.secondary",
                    maxWidth: 240,
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "primary.main",
                    fontWeight: 600,
                  },
                }}
              >
                {admissionsWithTerms.map((student, i) => {
                  const info = termGeneralInfoMap[student.id] || {};
                  const admissionContext = getManageAdmissionContext(
                    student,
                    info,
                    courseList,
                    uniData
                  );
                  return (
                    <Tab
                      key={student.id}
                      value={i}
                      label={
                        <Tooltip
                          title={<ManageAdmissionContextTooltip context={admissionContext} />}
                          {...manageAdmissionTabTooltipProps}
                        >
                          <Box component="span" sx={{ display: "inline-block" }}>
                            {admissionContext.shortTabLabel}
                          </Box>
                        </Tooltip>
                      }
                    />
                  );
                })}
              </Tabs>
            )}
            <DialogContent sx={{ overflow: "auto", pt: 3, px: 3, pb: 2 }}>
              {activeStudent && (
                <Box>
                  {applicationWithdrawn && (
                    <Box sx={{ mb: 2 }}>
                      <ApplicationWithdrawPanel
                        admissionId={activeStudent.id}
                        withdrawn
                        withdrawReason={activeAppData.applicationWithdrawnReason}
                        withdrawnAt={activeAppData.applicationWithdrawnAt}
                        withdrawnDocuments={activeAppData.applicationWithdrawnDocuments ?? []}
                        statusBeforeWithdrawal={activeAppData.statusBeforeWithdrawal}
                        onReactivated={() => handleApplicationReactivated(activeStudent.id)}
                      />
                    </Box>
                  )}
                  <TermScheduleTable
                    admissionContext={getManageAdmissionContext(
                      activeStudent,
                      generalInfo,
                      courseList,
                      uniData
                    )}
                    generalInfo={generalInfo}
                    setGeneralInfo={(updater) => {
                      scheduleEditedSinceOpenRef.current.add(activeStudent.id);
                      setTermGeneralInfoMap((prev) => ({
                        ...prev,
                        [activeStudent.id]:
                          typeof updater === "function"
                            ? updater(prev[activeStudent.id] || {})
                            : updater,
                      }));
                    }}
                    lockedTermIds={lockedIds}
                    rejectedTermIndices={rejectedIndices}
                    onSaveBeforeTask={() => saveTermScheduleForTask(activeStudent.id)}
                    onSaveTermSchedule={() => handleSaveTermSchedule(activeStudent.id)}
                    onRefreshLock={refetchVerifications}
                    scheduleReadOnly={applicationWithdrawn}
                  />
                </Box>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setManageAdmissionsOpen(false)}>Close</Button>
              <Button
                variant="contained"
                disabled={isSaving || applicationWithdrawn}
                onClick={() => handleSaveTermSchedule(activeStudent?.id)}
              >
                {isSaving ? "Saving…" : "Save Term Schedule"}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
      <Dialog
        open={verificationEditor.open}
        onClose={closeVerificationEditor}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Send Account Entry for Verification</DialogTitle>
        <DialogContent>
          {verificationEditor.detail && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Submitted values are locked until the account user rejects them. Verified requests
                are imported from the Account verification tab.
              </Typography>
              {verificationEditor.detail.module === "Admission" ? (
                <AdmissionVerificationEditor
                  detail={verificationEditor.detail}
                  onChange={updateAdmissionDraft}
                  onAddRow={addAdmissionDraftRow}
                  selectedAdmissionDetailIds={selectedAdmissionDetailIds}
                  onToggleSelected={(rowId) =>
                    setSelectedAdmissionDetailIds((prev) =>
                      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
                    )
                  }
                  verificationByAdmissionDetailId={verificationByAdmissionDetailId}
                />
              ) : (
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {accountFieldsFor([
                    "agreedAmount",
                    "cost",
                    "paidAmount",
                    "referral",
                    "discount",
                  ]).map((field) => (
                    <TextField
                      key={field}
                      size="small"
                      label={field}
                      value={verificationEditor.detail?.[field] ?? ""}
                      onChange={(event) => updateDraft({ [field]: event.target.value })}
                    />
                  ))}
                </Stack>
              )}
              <UploadFile
                data={receiptData}
                setData={setReceiptData}
                imageKey="receipts"
                label="Receipt"
                type="multiple"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVerificationEditor}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={stagedEditor.open} onClose={closeStagedEditor} maxWidth="lg" fullWidth>
        <DialogTitle>Add Account Entry for Verification</DialogTitle>
        <DialogContent>
          {stagedEditor.detail && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This will be sent to Account users for verification. It will appear in live Accounts
                only after import.
              </Typography>
              {stagedEditor.detail.module === "Admission" && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography variant="body2" color="warning.main">
                    Fees, dates, material and discounts are read-only here. To change them, update
                    the term schedule in Admissions - Offer letter received for this client.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openOfferLetterTermSchedule}
                    disabled={
                      !stagedEditor.detail?.admissionValues?.studentDbId &&
                      !admissionsForStagedEditor[0]?.id
                    }
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Open term schedule
                  </Button>
                </Stack>
              )}
              <TextField
                select
                size="small"
                label="Module"
                value={stagedEditor.detail.module || "RPL"}
                onChange={(event) => setStagedModule(event.target.value)}
                sx={{ maxWidth: 260 }}
              >
                {["RPL", "Admission", "Visa", "Insurance", "Skill Assessment"].map((module) => (
                  <MenuItem key={module} value={module}>
                    {module}
                  </MenuItem>
                ))}
              </TextField>
              {stagedEditor.detail.module === "Admission" ? (
                <StagedAdmissionEditor
                  detail={stagedEditor.detail}
                  admissions={admissionsForStagedEditor}
                  onSelectAdmission={setStagedAdmission}
                  onChange={updateStagedAdmissionRow}
                  selectedRowKeys={selectedStagedAdmissionRowKeys}
                  onToggleSelected={toggleStagedAdmissionRow}
                  verificationByTermKey={stagedAdmissionVerificationByTermKey}
                  loading={stagedAdmissionLoading}
                />
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                    maxWidth: 980,
                  }}
                >
                  {accountFieldsFor(STAGED_ACCOUNT_FIELDS).map((field) => (
                    <TextField
                      key={field.key}
                      size="small"
                      label={field.label}
                      value={stagedEditor.detail?.[field.key] ?? ""}
                      onChange={(event) => updateStagedDetail({ [field.key]: event.target.value })}
                      fullWidth
                    />
                  ))}
                </Box>
              )}
              <UploadFile
                data={stagedReceiptData}
                setData={setStagedReceiptData}
                imageKey="receipts"
                label="Receipt"
                type="multiple"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStagedEditor}>Cancel</Button>
          {stagedEditor.detail?.module === "Admission" &&
            stagedEditor.detail?.admissionValues?.studentDbId && (
              <Button
                variant="outlined"
                disabled={stagedAdmissionLoading}
                onClick={saveToTermSchedule}
              >
                Save to term schedule
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {openNotesModal.state && canViewAccountNotes && (
        <NotesModal
          open={openNotesModal.state}
          handleClose={() => {
            setOpenNotesModal({ state: false, data: null, subtable: false, parentId: null });
            refetchAccountData();
          }}
          data={openNotesModal.data}
          accountDetails={localAccountDetails}
          setAccountDetails={setLocalAccountDetails}
          subtable={openNotesModal.subtable}
          parentId={openNotesModal.parentId}
          onPaymentNoteDeleted={async (note) => {
            const cadId = openNotesModal.data?.id;
            const module = openNotesModal.data?.module;
            if (!cadId || module === "Admission" || !isPaymentNote(note)) return;

            const payments = modulePaymentsMap[cadId] || [];
            const payment = findPaymentForNote(note, payments);
            if (!payment) return;

            const next = payments.filter((p) => p.id !== payment.id);
            setModulePaymentsMap((prev) => ({ ...prev, [cadId]: next }));
            const totalPaid = next.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            setLocalAccountDetails((prev) =>
              prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
            );

            await syncCadPayments(
              cadId,
              next.map((p) => ({
                uuid: p.id,
                amount: p.amount,
                date: p.date,
                reference: p.reference,
                createdAt: p.createdAt,
              }))
            );
          }}
        />
      )}
      {paymentsModal.open && paymentsModal.row && isLinkedModuleRow(paymentsModal.row) && (
        <LinkedModulePaymentsModal
          open={paymentsModal.open}
          onClose={() => setPaymentsModal({ open: false, row: null })}
          cadId={paymentsModal.row.id}
          moduleName={paymentsModal.row.module || ""}
          agreedAmount={Number(paymentsModal.row.agreedAmount) || 0}
          discountAmount={Number(paymentsModal.row.discount) || 0}
          isOtherEntry={isOtherEntryRow(paymentsModal.row)}
          onPaymentsChanged={() => refetchAccountData?.()}
        />
      )}
      {paymentsModal.open && paymentsModal.row && !isLinkedModuleRow(paymentsModal.row) && (
        <GenericPaymentsModal
          open={paymentsModal.open}
          onClose={() => setPaymentsModal({ open: false, row: null })}
          moduleName={paymentsModal.row.module || ""}
          cadId={paymentsModal.row.id || null}
          agreedAmount={Number(paymentsModal.row.agreedAmount) || 0}
          discountAmount={Number(paymentsModal.row.discount) || 0}
          readOnly={!String(paymentsModal.row.module || "").trim()}
          readOnlyReason="Name this entry in the account editor first. A custom entry's payments go through account verification."
          clientId={clientData?.id ?? clientData?.clientId ?? id ?? null}
          clientName={clientData?.name ?? ""}
          payments={modulePaymentsMap[paymentsModal.row.id] || []}
          onChangePayments={(newPayments) => {
            const cadId = paymentsModal.row.id;
            setModulePaymentsMap((prev) => ({ ...prev, [cadId]: newPayments }));
            const totalPaid = newPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            setLocalAccountDetails((prev) =>
              prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
            );
          }}
          onPaymentsSynced={(synced) => {
            const cadId = paymentsModal.row.id;
            if (!Array.isArray(synced)) return;
            const rowNotes = localAccountDetails.find((r) => r.id === cadId)?.notes || [];
            const normalized = enrichPaymentsFromNotes(normalizeModulePayments(synced), rowNotes);
            setModulePaymentsMap((prev) => ({ ...prev, [cadId]: normalized }));
            const totalPaid = normalized.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            setLocalAccountDetails((prev) =>
              prev.map((r) => (r.id === cadId ? { ...r, paidAmount: String(totalPaid) } : r))
            );
          }}
          onAppendRowNote={async (note) => {
            const rowUuid = paymentsModal.row?.uuid;
            const cadId = paymentsModal.row?.id;
            if (!rowUuid) return;
            const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
            let savedNote = {
              ...note,
              createdBy: userDetail?.name ?? userDetail?.firstName ?? "User",
            };
            if (cadId && note.text) {
              try {
                const apiNote = await persistAccountDetailNote(cadId, note.text);
                if (apiNote) {
                  savedNote = {
                    ...savedNote,
                    id: apiNote.id,
                    uuid: apiNote.uuid ?? savedNote.uuid,
                    text: apiNote.text ?? apiNote.noteText ?? savedNote.text,
                  };
                }
              } catch (err) {
                // Rethrow only — the payments modal owns the single message for this outcome, and
                // toasting here too put a failure alongside its own success.
                throw err;
              }
            }
            setLocalAccountDetails((prev) =>
              prev.map((r) => {
                if (r.uuid !== rowUuid) return r;
                return {
                  ...r,
                  notes: [...(r.notes || []), savedNote],
                  notesCount: (r.notesCount || 0) + 1,
                };
              })
            );
          }}
          onRemoveRowNote={async (payment) => {
            const rowUuid = paymentsModal.row?.uuid;
            if (!rowUuid || !payment) return;
            const row = localAccountDetails.find((r) => r.uuid === rowUuid);
            const note = findNoteForPayment(payment, row?.notes || []);
            try {
              if (note?.id) {
                await axios.delete(`${API_URL}/notes/${note.id}?parentType=ACCOUNT_DETAIL`);
              }
              if (note) {
                setLocalAccountDetails((prev) =>
                  prev.map((r) => {
                    if (r.uuid !== rowUuid) return r;
                    const remaining = (r.notes || []).filter((n) => !isSameNote(n, note));
                    return {
                      ...r,
                      notes: remaining,
                      notesCount: Math.max(remaining.length, 0),
                    };
                  })
                );
              }
            } catch (err) {
              toast.error(err?.response?.data?.errorMessage || "Failed to delete linked note.");
              throw err;
            }
          }}
          rowNotes={
            localAccountDetails.find((r) => r.uuid === paymentsModal.row?.uuid)?.notes || []
          }
        />
      )}
    </div>
  );
};

export default ClientAccounts;

const AdmissionVerificationEditor = ({
  detail,
  onChange,
  onAddRow,
  selectedAdmissionDetailIds,
  onToggleSelected,
  verificationByAdmissionDetailId,
}) => {
  const rows = detail.admissionDetails || [];
  return (
    <Box sx={{ overflowX: "auto" }}>
      <div className="flex justify-end mb-2">
        <Button variant="outlined" size="small" onClick={onAddRow}>
          Add entry
        </Button>
      </div>
      <table className="min-w-full text-sm border border-app-border">
        <thead className="bg-app-surface-muted">
          <tr>
            <th className="text-left p-2">Send</th>
            <th className="text-left p-2">Term</th>
            <th className="text-left p-2">Verification</th>
            <th className="text-left p-2">Date</th>
            <th className="text-left p-2">Fee</th>
            <th className="text-left p-2">Material</th>
            <th className="text-left p-2">Discount</th>
            <th className="text-left p-2">Paid</th>
            <th className="text-left p-2">Due</th>
            {canViewAgencyFinancials() && <th className="text-left p-2">Referral</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const verification = row?.id ? verificationByAdmissionDetailId.get(row.id) : null;
            const disabled =
              !row?.id ||
              toNumber(row.paidAmount) <= 0 ||
              ["PENDING", "VERIFIED", "IMPORTED"].includes(verification?.status);
            return (
              <tr key={row.id || row.uuid || index} className="border-t border-app-border">
                <td className="p-2">
                  <Checkbox
                    size="small"
                    disabled={disabled}
                    checked={!!row?.id && selectedAdmissionDetailIds.includes(row.id)}
                    onChange={() => onToggleSelected(row.id)}
                  />
                </td>
                <td className="p-2">{row.terms || index + 1}</td>
                <td className="p-2">
                  <AccountVerificationChip
                    status={verification?.status}
                    rejectionFeedback={verification?.rejectionFeedback}
                  />
                </td>
                <td className="p-2">
                  <TextField
                    size="small"
                    value={row.date ?? ""}
                    onChange={(event) => onChange(index, { date: event.target.value })}
                  />
                </td>
                <td className="p-2">
                  <TextField
                    size="small"
                    value={row.perSemFee ?? ""}
                    onChange={(event) => onChange(index, { perSemFee: event.target.value })}
                  />
                </td>
                <td className="p-2">
                  <TextField
                    size="small"
                    value={row.materialFee ?? ""}
                    onChange={(event) => onChange(index, { materialFee: event.target.value })}
                  />
                </td>
                <td className="p-2">
                  <TextField
                    size="small"
                    value={row.discount ?? ""}
                    onChange={(event) => onChange(index, { discount: event.target.value })}
                  />
                </td>
                <td className="p-2">
                  <TextField
                    size="small"
                    value={row.paidAmount ?? ""}
                    onChange={(event) => onChange(index, { paidAmount: event.target.value })}
                  />
                </td>
                <td className="p-2">{formatMoney(row.dueAmount || 0)}</td>
                {canViewAgencyFinancials() && (
                  <td className="p-2">
                    <TextField
                      size="small"
                      value={row.referral ?? ""}
                      onChange={(event) => onChange(index, { referral: event.target.value })}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

const StagedAdmissionEditor = ({
  detail,
  admissions,
  onSelectAdmission,
  onChange,
  selectedRowKeys,
  onToggleSelected,
  verificationByTermKey,
  loading,
}) => {
  const rows = detail.admissionDetails || [];
  const studentId = detail.admissionValues?.studentDbId;
  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label="Admission"
        value={detail.admissionValues?.studentDbId || ""}
        onChange={(event) => onSelectAdmission(event.target.value)}
        disabled={loading}
        sx={{ maxWidth: 420 }}
      >
        {admissions.map((admission) => (
          <MenuItem key={admission.id} value={admission.id}>
            {admission.name || admission.clientName || `Admission #${admission.id}`}
          </MenuItem>
        ))}
      </TextField>
      {detail.admissionValues?.studentDbId && rows.length === 0 && (
        <Typography variant="body2" color="error">
          {loading
            ? "Loading latest admission term schedule..."
            : "No term schedule is available for the selected admission."}
        </Typography>
      )}
      {rows.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <table className="min-w-full text-sm border border-app-border">
            <thead className="bg-app-surface-muted">
              <tr>
                <th className="text-left p-2">Send</th>
                <th className="text-left p-2">Term</th>
                <th className="text-left p-2">Verification</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Fee</th>
                <th className="text-left p-2">Material</th>
                <th className="text-left p-2">Discount</th>
                <th className="text-left p-2">Paid</th>
                <th className="text-left p-2">Due</th>
                {canViewAgencyFinancials() && <th className="text-left p-2">Referral</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const key = stagedAdmissionRowKey(row, index);
                const verificationKey = stagedAdmissionVerificationKey(studentId, row, index);
                const existing = verificationKey
                  ? verificationByTermKey?.get(verificationKey)
                  : null;
                const status = existing?.verification?.status;
                const locked = isActiveStagedVerification(status);
                const displayRow =
                  locked && existing?.detail ? { ...row, ...existing.detail } : row;
                const paid = toNumber(displayRow.paidAmount);
                const agreed =
                  toNumber(displayRow.perSemFee) +
                  toNumber(displayRow.materialFee) -
                  toNumber(displayRow.discount);
                const meetsMinimum = agreed <= 0 || paid >= agreed;
                return (
                  <tr key={row.uuid || index} className="border-t border-app-border">
                    <td className="p-2">
                      <Checkbox
                        size="small"
                        disabled={locked || paid <= 0 || !meetsMinimum}
                        checked={
                          !locked && paid > 0 && meetsMinimum && selectedRowKeys.includes(key)
                        }
                        onChange={() => onToggleSelected(row, index)}
                      />
                    </td>
                    <td className="p-2">{displayRow.terms || index + 1}</td>
                    <td className="p-2">
                      {status ? (
                        <AccountVerificationChip
                          status={status}
                          rejectionFeedback={existing?.verification?.rejectionFeedback}
                        />
                      ) : (
                        <Chip size="small" label="Not sent" variant="outlined" />
                      )}
                    </td>
                    <td className="p-2">{displayRow.date || "-"}</td>
                    <td className="p-2">{formatMoney(displayRow.perSemFee || 0)}</td>
                    <td className="p-2">{formatMoney(displayRow.materialFee || 0)}</td>
                    <td className="p-2">{formatMoney(displayRow.discount || 0)}</td>
                    <td className="p-2">
                      <TextField
                        size="small"
                        disabled={locked}
                        value={displayRow.paidAmount ?? ""}
                        onChange={(event) => onChange(index, { paidAmount: event.target.value })}
                      />
                    </td>
                    <td className="p-2">{formatMoney(displayRow.dueAmount || 0)}</td>
                    {canViewAgencyFinancials() && (
                      <td className="p-2">
                        <TextField
                          size="small"
                          disabled={locked}
                          value={displayRow.referral ?? ""}
                          onChange={(event) => onChange(index, { referral: event.target.value })}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Stack>
  );
};
